import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Grid,
    OutlinedInput
} from '@material-ui/core';
import PaymentMethodModal from 'components/provider/PaymentMethodModal';
import PaymentModal from 'components/provider/PaymentModal';
import DeliverySummary from 'components/provider/DeliverySummary';
import ProviderAPI from 'config/api/ProviderAPI';
import getLeadPrice from 'config/helpers/leadPricing';
import { onError } from 'config/lib/errorLib';
import { NotificationManager } from 'react-notifications';
import eeFav from 'resources/images/ee-fav.png'
import { Link } from 'react-router-dom';

function SelectPlan({ companyDetails, updateCompany }) {
    const api = new ProviderAPI()
    const [activeStep, setActiveStep] = useState(0); // Using activeStep for section navigation
    const coverageType = (companyDetails.coverageType || 'counties').toLowerCase();
    const countyCount = (companyDetails.counties || []).length;
    const planType = (companyDetails.planType || 'weekly').toLowerCase();
    const isTopup = planType === 'topup';
    const defaultLeads = isTopup ? 40 : 20;
    const companyLeadPrice = companyDetails.planDetails && companyDetails.planDetails.leadPrice;
    const leadPrice = (companyLeadPrice != null && companyLeadPrice !== '')
        ? Number(companyLeadPrice)
        : getLeadPrice(coverageType, countyCount);
    const [selectedLeads, setSelectedLeads] = useState(defaultLeads);
    const [weeklyMaxLeads, setWeeklyMaxLeads] = useState(5);
    const [showPaymentMethod, setShowPaymentMethod] = useState(false)
    const [showTopupPayment, setShowTopupPayment] = useState(false)
    const [loading, setLoading] = useState(false)
    const [agreeTerms, setAgreeTerms] = useState(false); // New state for terms agreement

    async function submitPlanDetails() {
        setLoading(true)
        try {
            const details = {
                maxLeads: isTopup ? weeklyMaxLeads : selectedLeads,
                planActive: true
            }
            const company = await api.updatePlanDetails(companyDetails.companyId, details)
            updateCompany(company)
            NotificationManager.success('Plan settings updated')
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    const renderStepContent = () => {
        if (isTopup) {
            // Top-up: admin-assigned flow — user buys a fixed block of leads with a weekly delivery cap
            return (
                <Card>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <img src={eeFav} alt="ee-fav" style={{ width: '100%', boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', borderRadius: '5px' }} />
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <Typography variant="h3">Account Topup</Typography>
                                <Typography variant="body2" style={{ marginTop: '0.5rem' }}>
                                    Purchase a block of {selectedLeads} verified leads. Choose a weekly cap below. No more than this number of leads will be delivered in any single week until your balance reaches zero.
                                </Typography>
                                <FormControl variant="filled" width="50%" component={Box} style={{ marginTop: '1.25rem' }}>
                                    <FormLabel style={{ marginBottom: '0.25rem' }}>Weekly max leads</FormLabel>
                                    <Select
                                        value={weeklyMaxLeads}
                                        onChange={(e) => setWeeklyMaxLeads(e.target.value)}
                                        input={<OutlinedInput />}
                                    >
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={30}>30</MenuItem>
                                        <MenuItem value={40}>40</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Box mt={3}>
                            <Typography variant="body2">✅ All leads are verified</Typography>
                            <Typography variant="body2">✅ Leads will be within your selected counties</Typography>
                            <Typography variant="body2">✅ One-off top-up purchase (immediate charge)</Typography>
                        </Box>
                        <Box mt={5}>
                            <Typography variant="body2">Subtotal: €{(selectedLeads * leadPrice).toFixed(2)}</Typography>
                            <Typography variant="body2">VAT (23%): €{(selectedLeads * leadPrice * 0.23).toFixed(2)}</Typography>
                            <Typography variant="body2" style={{fontWeight: 'bold', color: '#32325d' }}>Total: €{(selectedLeads * leadPrice * 1.23).toFixed(2)}</Typography>
                        </Box>
                        <Box mt={3}>
                            <FormControl>
                                <Grid container alignItems="center">
                                    <Grid item>
                                        <input
                                            type="checkbox"
                                            id="agreeTermsTopup"
                                            checked={agreeTerms}
                                            onChange={(e) => setAgreeTerms(e.target.checked)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <Typography variant="body2" htmlFor="agreeTermsTopup" style={{ display: 'inline' }}>
                                            I agree to the
                                            <Link to="/terms-conditions" target="_blank" style={{ textDecoration: 'none', color: '#229626', marginLeft: '4px' }}>
                                                Terms & Conditions
                                            </Link>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </FormControl>
                        </Box>
                        <Box mt={3} display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowTopupPayment(true)}
                                disabled={!agreeTerms || !weeklyMaxLeads}
                            >
                                Pay now
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )
        }
        if (activeStep === 0) {
            return (
                <Card>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <img src={eeFav} alt="ee-fav" style={{ width: '100%', boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', borderRadius: '5px' }} />
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <Typography variant="h3">Select number of leads per week</Typography>
                                <FormControl variant="filled" width="50%" component={Box}>
                                    <Select
                                        value={selectedLeads}
                                        onChange={(e) => setSelectedLeads(e.target.value)}
                                        input={<OutlinedInput />}
                                    >
                                        <MenuItem value="" disabled>
                                            Select leads
                                        </MenuItem>
                                        {[10, 20, 25, 30, 40, 50, 100, 250, 500].map((lead) => (
                                            <MenuItem key={lead} value={lead}>
                                                {lead}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Box mt={3}>
                            <Typography variant="body2">✅ All leads are verified</Typography>
                            <Typography variant="body2">✅ Leads will be within your selected counties</Typography>
                            <Typography variant="body2">✅ Payment is weekly in arrears (Sat–Fri, charged Monday)</Typography>
                        </Box>
                        <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" style={{fontWeight: 'bold', color: '#32325d' }}>
                                Subtotal: €{selectedLeads ? (selectedLeads * leadPrice).toFixed(2) : '0.00'}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setActiveStep(1)}
                                disabled={!selectedLeads} // Disable button until a selection is made
                            >
                                Next
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            );
        } else if (activeStep === 1) {
            return (
                <Card>
                    <CardContent>
                        <DeliverySummary
                            companyDetails={companyDetails}
                            selectedLeads={selectedLeads}
                        />
                        <Box mt={2}>
                        <FormControl>
                            <Grid container alignItems="center">
                                <Grid item>
                                    <input
                                        type="checkbox"
                                        id="agreeTerms"
                                        checked={agreeTerms}
                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <Typography variant="body2" htmlFor="agreeTerms" style={{ display: 'inline' }}>
                                        I agree to the
                                        <Link to="/terms-conditions" target="_blank" style={{ textDecoration: 'none', color: '#229626', marginLeft: '4px' }}>
                                            Terms & Conditions
                                        </Link>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </FormControl>
                    </Box>
                        <Box mt={3} display="flex" justifyContent="space-between">
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setActiveStep(0)}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={loading || !agreeTerms} // Button disabled until terms are agreed
                                onClick={() => setShowPaymentMethod(true)}
                            >
                                Add payment method
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            );
        }
    };

    return (
        <Box mt={3}>
            <Grid container spacing={3} justifyContent="center">
                <Grid item md={6} xs={12}>
                    {renderStepContent()}
                </Grid>
            </Grid>

            {showPaymentMethod && !isTopup && (
                <PaymentMethodModal
                    companyDetails={companyDetails}
                    open={showPaymentMethod}
                    onClose={() => setShowPaymentMethod(false)}
                    showSuccessAlert={() => submitPlanDetails()}
                />
            )}
            {showTopupPayment && isTopup && (
                <PaymentModal
                    open={showTopupPayment}
                    onClose={() => setShowTopupPayment(false)}
                    companyDetails={companyDetails}
                    leadCount={selectedLeads}
                    onSuccess={() => {
                        setShowTopupPayment(false)
                        NotificationManager.success('Top-up payment successful')
                        submitPlanDetails()
                    }}
                />
            )}
        </Box>
    );
}

export default SelectPlan;
